import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

export default function SEO({ title, description, image, date, updated, alternates }) {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const siteTitle = "Mupax OÜ";
  const domain = "https://mupax.eu";
  
  // 1. Määra sisu
  const fullTitle = title ? `${title} | ${siteTitle}` : (t('seo.title') || siteTitle);
  const metaDesc = description || t('seo.description') || "Mupax - Scalable Software Engineering";
  const metaImage = image ? `${domain}${image}` : `${domain}/og-image.jpg`;

  // 2. Arvuta URL-id - kasuta alati tegelikku location.pathname kanonilise URL-i jaoks
  const actualPath = location.pathname;

  // Puhastame teekonna keelest (/et/services -> /services)
  let cleanPath = actualPath.replace(/^\/(en|et)/, '');
  if (cleanPath === '') cleanPath = '/';

  const fullUrl = `${domain}${actualPath}`;

  // 3. ERIJUHT: BLOGI
  const isBlog = actualPath.includes('/blog');

  // 4. KANOONILINE URL - iga leht viitab iseendale (iseviitav kanooniline).
  // Blogi on nüüd kakskeelne, seega ka blogipostitus viitab iseendale; keelte
  // sidumine käib hreflang-i kaudu (alternates).
  const canonicalUrl = fullUrl;

  // Blog hreflang pairs (only when a post has a translation).
  const enHref = alternates?.find((a) => a.hreflang === 'en')?.href;
  const etHref = alternates?.find((a) => a.hreflang === 'et')?.href;

  // 5. Locale
  const currentLocale = i18n.language === 'et' ? 'et_EE' : 'en_US';
  const alternateLocale = i18n.language === 'et' ? 'en_US' : 'et_EE';

  // Abimuutuja URL-i lõpu jaoks (et vältida topelt kaldkriipse)
  const pathSuffix = cleanPath === '/' ? '' : cleanPath;

  return (
    <Helmet>
      {/* --- Standard Meta --- */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDesc} />
      <link rel="canonical" href={canonicalUrl} />

      {/* --- Hreflang (PARANDUS: Eemaldatud Fragment <>, iga rida eraldi) --- */}
      {!isBlog && (
        <link rel="alternate" hreflang="en" href={`${domain}/en${pathSuffix}`} />
      )}
      {!isBlog && (
        <link rel="alternate" hreflang="et" href={`${domain}/et${pathSuffix}`} />
      )}
      {!isBlog && (
        <link rel="alternate" hreflang="x-default" href={`${domain}/en${pathSuffix}`} />
      )}

      {/* Blog (bilingual posts): hreflang pairs when a translation exists */}
      {isBlog && enHref && (
        <link rel="alternate" hreflang="en" href={`${domain}${enHref}`} />
      )}
      {isBlog && etHref && (
        <link rel="alternate" hreflang="et" href={`${domain}${etHref}`} />
      )}
      {isBlog && enHref && (
        <link rel="alternate" hreflang="x-default" href={`${domain}${enHref}`} />
      )}

      {/* --- Open Graph --- */}
      <meta property="og:site_name" content={siteTitle} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDesc} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:type" content={isBlog ? 'article' : 'website'} />
      {isBlog && date && <meta property="article:published_time" content={date} />}
      {isBlog && (updated || date) && <meta property="article:modified_time" content={updated || date} />}
      {isBlog && <meta property="article:author" content="Mupax OÜ" />}

      <meta property="og:locale" content={currentLocale} />
      {!isBlog && <meta property="og:locale:alternate" content={alternateLocale} />}

      {/* --- Twitter --- */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDesc} />
      <meta name="twitter:image" content={metaImage} />
    </Helmet>
  );
}
